import React, {useEffect, useRef, useState} from "react";
import {ClearButton, Container, LoadingBox, Toggle} from "./components/styles";
import Dropdown from "./components/dropdown";
import Caption from "./components/caption";
import {str_replace, isEmpty, convertToFarsiChar, handleLockBrowserScroll} from "@helpers/helpers";
import {toEnglishNumber} from "@helpers/numbers";
import CircleLoading from "@molecules/lotties/circle_loading";
import usePrevious from "@hooks/previous";
import Icon from "@atoms/icon";
import CloseIcon from "@icons/close.svg";

function Selection({type, name, label, options, unit, onChange, onShow, onHide, selected_range, selected_items, addSelectedClass = true, searchable, clearabel = true, limit_select, select_optional = false, caption_show, showRangeInputInCaption, showLabelAndSelected, showSelectedItemsInTop, appendSelectedItemsInTop = false, loading = false, disabled = false, icon = null, perfectScrollbar = true, scroll_lock = true}) {
    const Ref = useRef(null);
    const dropdownRef = useRef('');
    const toggleRef = useRef('');
    const [dropdownToggle, setDropdownToggle] = useState(false);
    const prevItems = usePrevious(options);
    const [items, setItems] = useState({options: options});
    const [selected, setSelected] = useState({items: []});
    const [selectedRange, setSelectedRange] = useState({
        min: null,
        max: null,
    });
    const [itemsPosition, setItemsPosition] = useState("right");
    const [containerClassName, setContainerClassName] = useState('');

    useEffect(() => {
        if (dropdownToggle) {

            if (searchable) {
                handleSearch('');
            }

            const bodyClasses = document.body.classList;

            if (!bodyClasses.contains('filter-open')) {
                window.history.pushState(null, "", window.location.href);
            }

            window.onpopstate = () => {
                if (dropdownToggle) {

                    if (bodyClasses.contains('selection-open')) {
                        window.history.pushState('back', null, null);
                        handleToggle(false);
                    } else if (bodyClasses.contains('filter-open')) {
                        bodyClasses.remove('filter-open');
                        document.querySelector('.modal-filters-card').classList.remove('show');
                        document.querySelector('.modal-filters').classList.remove('show');
                    }
                }
            };

            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [Ref, dropdownToggle]);

    useEffect(() => {
        if (prevItems && !dropdownToggle) {
            let difference = options
                .filter((x) => !prevItems.includes(x))
                .concat(prevItems.filter((x) => !options.includes(x)));

            if (typeof difference.length !== "undefined" && difference.length) {
                setItems({options: options});
            }
        }

        if (typeof selected_range !== "undefined") {
            setSelectedRange(selected_range);
        }
    });

    function handleClickOutside(event) {
        if (Ref.current && !Ref.current.contains(event.target) && dropdownRef.current.classList.contains('show-dropdown')) {
            handleToggle(false);
        }
    }

    const handleToggle = (show, with_on_change = true, changed_value) => {
        const bodyClasses = document.body.classList;
        const searchFilterWrapper = document.querySelector('.search-filter-wrapper');

        if (show) {
            setItemsPosition("right");
            bodyClasses.add("selection-open");

            if (!isEmpty(searchFilterWrapper)) {
                searchFilterWrapper.classList.add("selection-opened");
            }

            if (scroll_lock) {
                handleLockBrowserScroll(true);
            }
        } else {
            setTimeout(() => {
                bodyClasses.remove("selection-open");
            }, 400);

            if (!isEmpty(searchFilterWrapper)) {
                searchFilterWrapper.classList.remove("selection-opened");
            }

            const checkBodyClasses = (!bodyClasses.contains('modal-open') &&
                !bodyClasses.contains('menu-open') &&
                !bodyClasses.contains('filter-open') &&
                !bodyClasses.contains('swiper-open'));

            if (checkBodyClasses) {
                handleLockBrowserScroll(false);
            }
        }

        if (typeof onShow !== "undefined") {
            onShow(show);
        }
        if (typeof onHide !== "undefined") {
            onHide(!show);
        }

        if (show) {
            dropdownRef.current.classList.add('show-dropdown');
        } else {
            dropdownRef.current.classList.remove('show-dropdown');
        }

        setDropdownToggle(show);

        if (!show && with_on_change) {
            switch (type) {
                case "range":
                    onChange(selectedRange);
                    break;
                case "single":
                    if (select_optional) {
                        onChange(selected);
                    } else if (typeof changed_value !== "undefined") {
                        onChange(changed_value);
                    }
                    break;
                case "multiple":
                    onChange(selected);
                    break;
            }
        }
    };

    const handleItems = (value = null, change_position = true, set_toggle = true) => {
        let new_items = items.options;

        switch (type) {
            case "single":
                if (value !== null) {

                    for (let key in new_items) {
                        new_items[key]["checked"] = false;

                        if (new_items[key]["value"] !== undefined && new_items[key]["value"] == value) {
                            new_items[key]["checked"] = true;
                        }
                    }

                    if ((typeof value === "number" && value > 0)) {
                        setSelected({items: [value]});

                        if (!select_optional) {
                            handleToggle(false, true, {items: [value]});
                        }
                    } else if (typeof value !== "number") {
                        setSelected({items: [value]});

                        if (!select_optional) {
                            handleToggle(false, true, {items: [value]});
                        }
                    } else if (!select_optional) {
                        handleToggle(false, true, {items: []});
                    }
                }

                if (typeof value === "number" && value > 0 && addSelectedClass) {
                    Ref.current.classList.add('selected');
                } else if (typeof value !== "number" && !isEmpty(value) && addSelectedClass) {
                    Ref.current.classList.add('selected');
                } else {
                    Ref.current.classList.remove('selected');
                }

                break;

            case "multiple":
                let new_selected = selected.items;

                for (let key2 in new_items) {
                    if (!isEmpty(new_items[key2]["value"]) && !isEmpty(value) && new_items[key2]["value"] === value) {
                        new_items[key2]["checked"] = !new_items[key2]["checked"];

                        if (new_selected.length >= limit_select) {
                            new_items[key2]["checked"] = false;
                        }
                    }

                    const check_index = new_selected.indexOf(new_items[key2]["value"]);
                    if (check_index > -1 && new_items[key2]["checked"] === false) {
                        new_selected.splice(check_index, 1);
                    } else if (!(check_index > -1) && new_items[key2]["checked"] === true) {
                        new_selected.push(new_items[key2]["value"]);
                    }
                }

                setSelected({items: new_selected});

                if (new_selected.length && addSelectedClass) {
                    Ref.current.classList.add('selected');
                } else {
                    Ref.current.classList.remove('selected');
                }

                break;
            case "range":
                if (value !== null) {
                    let new_selected_range = selectedRange;

                    value = Number(str_replace(',', '', value));

                    if (itemsPosition === "right") {
                        new_selected_range.min = value;
                        if (change_position) {
                            setItemsPosition("left");
                        }
                    }

                    if (itemsPosition === "left") {
                        new_selected_range.max = value;
                    }

                    setSelectedRange(new_selected_range);

                    if (addSelectedClass && ((!isEmpty(new_selected_range.min) && new_selected_range.min > 0) || (!isEmpty(new_selected_range.max) && new_selected_range.max > 0))) {
                        Ref.current.classList.add('selected');
                    } else {
                        Ref.current.classList.remove('selected');
                    }
                }
                break;
        }

        setItems({options: new_items});

        if (type === "range" && !isEmpty(value) && set_toggle && itemsPosition == "left") {
            handleToggle(false);
        }
    };

    const handleSearch = (pattern) => {
        pattern = convertToFarsiChar(toEnglishNumber(pattern));

        let updatedList = [...options];

        const dropdownLists = dropdownRef.current.querySelector('.selection-lists');
        const allItems = dropdownLists.querySelectorAll('li');

        if (!isEmpty(pattern)) {

            for (let li of allItems) {
                li.classList.add('d-none');
            }

            updatedList = options.filter((item) => {
                return (item.checked || item.label.toString().toLowerCase().search(pattern) !== -1);
            });

            setItems({options: updatedList});

            let searchList = options.filter((item) => {
                return (item.label.toString().toLowerCase().search(pattern) !== -1);
            });

            for (let item of searchList) {
                const li = dropdownLists.querySelector('li.' + name + item.value);

                if (!isEmpty(li)) {
                    li.classList.remove('d-none')
                }
            }
        } else {
            for (let li of allItems) {
                li.classList.remove('d-none');
            }

            if (!isEmpty(dropdownRef.current) && !isEmpty(dropdownRef.current.querySelector('.selection-search'))) {
                dropdownRef.current.querySelector('.selection-search').value = '';

                const clearSearch = dropdownRef.current.querySelector('.js-clear-search');
                if (!isEmpty(clearSearch)) {
                    clearSearch.classList.add('d-none');
                }
            }

            dropdownRef.current.querySelector('.selection-lists').scrollTop = 0;

            const newList = options.sort(function (a, b) {
                return (a.checked === b.checked) ? 0 : a.checked ? -1 : 1;
            });

            setItems({options: newList});
        }
    };

    const handleClear = () => {
        if (!isEmpty(Ref.current)) {
            Ref.current.classList.remove('selected');

            if (!isEmpty(Ref.current.querySelector('.selected-label'))) {
                Ref.current.querySelector('.selected-label').innerHTML = '';
            }
        }

        if (type === "range") {
            const range = {min: null, max: null};
            onChange(range);
        } else {
            onChange({items: []});
        }
    };

    useEffect(() => {

        if (!isEmpty(selected_items)) {
            setSelected({items: selected_items});
        }

        if (!isEmpty(selected_range)) {
            setSelectedRange(selected_range)
        }

        let classes = ((dropdownToggle ? "show " : "") +
            ((addSelectedClass && !isEmpty(selected_items) && selected_items.length > 0) ? "selected " : "") +
            (((selectedRange.min !== null && selectedRange.min > 0 && selectedRange.min !== "") || (selectedRange.max !== null && selectedRange.max > 0 && selectedRange.max !== "")) ? "selected " : ""));
        setContainerClassName(classes);

    }, [selected_items, selectedRange]);

    let itemsOptions = items.options;

    if (appendSelectedItemsInTop && process.browser && !document.body.classList.contains('selection-open')) {
        itemsOptions.sort(function (a, b) {
            return (a.checked === b.checked) ? 0 : a.checked ? -1 : 1;
        });
    }

    useEffect(() => {
        if (name === 'facilities' && !selected_items.length) {
            Ref.current.classList.remove('selected');
        }
    }, [selected_items]);

    return (
        <Container
            className={'selection rounded-8 ' + (containerClassName ?? '')}
            ref={Ref}
            showRangeInputInCaption={showRangeInputInCaption}
            clearabel={clearabel}
        >
            <Toggle
                ref={toggleRef}
                onClick={(e) => ((!toggleRef.current.classList.contains('disabled')) ? handleToggle(!dropdownToggle) : false)}
                className={"selection-toggle d-flex align-items-center position-relative " + (loading ? ' is-loading disabled' : '') + (disabled ? ' disabled ' : '')}
                showRangeInputInCaption={showRangeInputInCaption}
            >
                <Caption
                    type={type}
                    label={label}
                    unit={unit}
                    items={items}
                    caption_show={caption_show}
                    selected_range={selectedRange}
                    handleItems={handleItems}
                    setItemsPosition={setItemsPosition}
                    showRangeInputInCaption={showRangeInputInCaption}
                    showLabelAndSelected={showLabelAndSelected}
                    clearabel={clearabel}
                />

                {
                    (loading) ?
                        <LoadingBox>
                            <CircleLoading width={16} height={16}/>
                        </LoadingBox>
                        : ''
                }
            </Toggle>

            {
                (clearabel && !loading) ?
                    <ClearButton className="rounded-circle selection-clear-button" onClick={() => handleClear()} ariaLabel="left close">
                        <Icon width={7} height={7}>
                            <CloseIcon/>
                        </Icon>
                    </ClearButton>
                    : ''
            }

            <Dropdown
                type={type}
                label={label}
                name={name}
                items={itemsOptions}
                show={dropdownToggle}
                setShow={handleToggle}
                searchable={searchable}
                handleSearch={handleSearch}
                handleItems={handleItems}
                selected_range={selectedRange}
                selected_items={selected_items}
                itemsPosition={itemsPosition}
                setItemsPosition={setItemsPosition}
                select_optional={select_optional}
                dropdownRef={dropdownRef}
                icon={icon}
                showSelectedItemsInTop={showSelectedItemsInTop}
                limit_select={limit_select}
                perfectScrollbar={perfectScrollbar}
            />
        </Container>
    );
}

export default Selection;
